import React, { useEffect, useState, useCallback } from "react";
import { api, apiErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, SectionCard } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, Plus, ThumbsUp, Trash2, UserX, Trophy } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const statusColor = {
  New: "bg-primary/10 text-primary border-primary/20",
  Reviewing: "bg-warning/10 text-warning border-warning/30",
  Implemented: "bg-success/10 text-success border-success/20",
  Declined: "bg-destructive/10 text-destructive border-destructive/20",
};

export default function Suggestions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isMgr = user?.role === "admin" || user?.role === "manager";
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ suggestion_categories: [], suggestion_statuses: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "Other", anonymous: false });

  const load = useCallback(() => api.get("/suggestions").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/meta/feature-constants").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const create = async () => {
    try {
      await api.post("/suggestions", form);
      toast.success("Suggestion submitted — thank you!");
      setOpen(false);
      setForm({ title: "", description: "", category: "Other", anonymous: false });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const upvote = async (s) => { await api.post(`/suggestions/${s.id}/upvote`); load(); };
  const setStatus = async (s, status) => { await api.put(`/suggestions/${s.id}/status`, { status }); toast.success(`Marked ${status}`); load(); };
  const del = async (s) => { if (window.confirm("Delete this suggestion?")) { await api.delete(`/suggestions/${s.id}`); load(); } };

  return (
    <div className="animate-fade-up">
      <PageTitle overline="Employee Voice" title="Suggestions">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-suggestion-btn"><Plus className="h-4 w-4 mr-1.5" /> New Suggestion</Button></DialogTrigger>
          <DialogContent className="bg-popover max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">Suggest an Improvement</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" placeholder="e.g. Flexible Friday hours" data-testid="suggestion-title" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="mt-1.5" data-testid="suggestion-category"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">{(meta.suggestion_categories || ["Other"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" placeholder="Describe your idea and how it helps the company." data-testid="suggestion-desc" /></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.anonymous} onCheckedChange={(v) => setForm({ ...form, anonymous: !!v })} data-testid="suggestion-anon" />
                Submit anonymously
              </label>
            </div>
            <DialogFooter><Button onClick={create} disabled={!form.title || !form.description} data-testid="suggestion-submit">Submit</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageTitle>

      <SectionCard title={`All Suggestions (${items.length})`}>
        {items.some((s) => (s.upvote_count || 0) > 0) && (
          <div className="mb-6 rounded-md border border-border bg-muted/40 p-4" data-testid="suggestion-leaderboard">
            <p className="overline mb-3 inline-flex items-center gap-1.5"><Trophy className="h-4 w-4 text-warning" /> Top Voted Ideas</p>
            <div className="space-y-2">
              {[...items].sort((a, b) => (b.upvote_count || 0) - (a.upvote_count || 0)).filter((s) => (s.upvote_count || 0) > 0).slice(0, 5).map((s, i) => (
                <div key={s.id} className="flex items-center gap-3" data-testid={`leaderboard-${s.id}`}>
                  <span className="font-heading font-extrabold text-lg w-6 text-muted-foreground">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{s.title}</p>
                    <span className="overline">{s.category} · {s.status}</span>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold"><ThumbsUp className="h-4 w-4 text-success" /> {s.upvote_count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-4">
          {items.length === 0 && <p className="col-span-full text-center text-muted-foreground py-12">No suggestions yet. Be the first to share an idea!</p>}
          {items.map((s) => (
            <Card key={s.id} className="p-5 rounded-md border-border" data-testid={`suggestion-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="overline">{s.category}</span>
                    <span className={cn("px-2 py-0.5 rounded text-xs border", statusColor[s.status])}>{s.status}</span>
                  </div>
                  <h3 className="font-heading font-bold text-lg mt-1">{s.title}</h3>
                </div>
                <Button size="sm" variant={s.upvoted ? "default" : "outline"} onClick={() => upvote(s)} className="shrink-0" data-testid={`upvote-${s.id}`}>
                  <ThumbsUp className="h-4 w-4 mr-1" /> {s.upvote_count}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{s.description}</p>
              {s.admin_comment && <p className="text-xs mt-2 p-2 rounded bg-muted border border-border"><span className="font-medium">HR:</span> {s.admin_comment}</p>}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  {s.anonymous && <UserX className="h-3 w-3" />}{s.author_name} · {fmtDate(s.created_at)}
                </span>
                {isMgr && (
                  <div className="flex items-center gap-1.5">
                    <Select value={s.status} onValueChange={(v) => setStatus(s, v)}>
                      <SelectTrigger className="h-8 w-32 text-xs" data-testid={`status-${s.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-popover">{(meta.suggestion_statuses || ["New", "Reviewing", "Implemented", "Declined"]).map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
                    </Select>
                    {isAdmin && <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => del(s)} data-testid={`del-suggestion-${s.id}`}><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}
